import { useState } from "react";
import { ChevronDown, Bookmark, Copy, RotateCcw, Eye, EyeOff } from "lucide-react";
import { getDocumentTypeIcon } from "../lib/documentCategorizer";

interface Citation {
  id: string;
  text: string;
  type: string;
  confidence: number;
  context: string;
  lineNumber: number;
}

interface DocumentAnalysis {
  category: string;
  confidence: number;
  indicators: string[];
  suggestedFormat: string;
  contentSummary: string;
  citations: Citation[];
  keyPhrases: string[];
  wordCount: number;
  readingLevel: string;
  complexity: number;
}

interface UploadedDocumentSummaryProps {
  file: File;
  analysis: DocumentAnalysis;
  content: string;
  onReset: () => void;
  onImport?: () => void;
}

export default function UploadedDocumentSummary({
  file,
  analysis,
  content,
  onReset,
  onImport,
}: UploadedDocumentSummaryProps) {
  const [bookmarkedSections, setBookmarkedSections] = useState<Set<number>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(["overview"]));
  const [showFullContent, setShowFullContent] = useState(false);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const toggleBookmark = (index: number) => {
    const newBookmarks = new Set(bookmarkedSections);
    if (newBookmarks.has(index)) {
      newBookmarks.delete(index);
    } else {
      newBookmarks.add(index);
    }
    setBookmarkedSections(newBookmarks);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const confidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return "text-green-300";
    if (confidence >= 0.6) return "text-yellow-300";
    return "text-orange-300";
  };

  const contentPreview = content.length > 500 ? content.substring(0, 500) + "..." : content;

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 p-5 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 shadow-lg">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">{getDocumentTypeIcon(analysis.category)}</span>
            <div>
              <p className="text-white font-semibold">{file.name}</p>
              <p className="text-white/60 text-sm capitalize">{analysis.category.replace(/_/g, ' ')}</p>
            </div>
          </div>
          <div className="flex gap-4 text-sm text-white/70 mt-3">
            <span>{analysis.wordCount} words</span>
            <span>•</span>
            <span>{analysis.readingLevel.charAt(0).toUpperCase() + analysis.readingLevel.slice(1)} reading level</span>
            <span>•</span>
            <span>Complexity: {analysis.complexity}%</span>
          </div>
        </div>
        <button
          onClick={onReset}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white/80 transition-all duration-200"
          title="Upload another file"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Overview Section */}
          <div className="rounded-xl bg-white/10 backdrop-blur-md border border-white/20 shadow-lg overflow-hidden">
            <button
              onClick={() => toggleSection("overview")}
              className="w-full flex items-center justify-between p-4 hover:bg-white/15 transition-colors duration-200"
            >
              <h3 className="text-white font-semibold">Document Overview</h3>
              <ChevronDown
                className={`w-5 h-5 text-white/60 transition-transform duration-300 ${
                  expandedSections.has("overview") ? "rotate-180" : ""
                }`}
              />
            </button>

            {expandedSections.has("overview") && (
              <div className="border-t border-white/10 p-4 space-y-4">
                {/* Category */}
                <div>
                  <p className="text-white/70 text-sm font-medium mb-2">Document Category</p>
                  <div className="flex items-center justify-between">
                    <span className="text-white capitalize">{analysis.category.replace(/_/g, ' ')}</span>
                    <span className={`font-semibold ${confidenceColor(analysis.confidence)}`}>
                      {Math.round(analysis.confidence * 100)}% confidence
                    </span>
                  </div>
                </div>

                {/* Suggested Format */}
                <div>
                  <p className="text-white/70 text-sm font-medium mb-2">Suggested Citation Format</p>
                  <div className="inline-block px-3 py-1 rounded-lg bg-white/15 border border-white/20">
                    <span className="text-white font-medium">{analysis.suggestedFormat}</span>
                  </div>
                </div>

                {/* Summary */}
                <div>
                  <p className="text-white/70 text-sm font-medium mb-2">Content Summary</p>
                  <p className="text-white/80 text-sm leading-relaxed">{analysis.contentSummary}</p>
                </div>
              </div>
            )}
          </div>

          {/* Indicators Section */}
          <div className="rounded-xl bg-white/10 backdrop-blur-md border border-white/20 shadow-lg overflow-hidden">
            <button
              onClick={() => toggleSection("indicators")}
              className="w-full flex items-center justify-between p-4 hover:bg-white/15 transition-colors duration-200"
            >
              <h3 className="text-white font-semibold">Detection Indicators ({analysis.indicators.length})</h3>
              <ChevronDown
                className={`w-5 h-5 text-white/60 transition-transform duration-300 ${
                  expandedSections.has("indicators") ? "rotate-180" : ""
                }`}
              />
            </button>

            {expandedSections.has("indicators") && (
              <div className="border-t border-white/10 p-4">
                <ul className="space-y-2">
                  {analysis.indicators.map((indicator, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-white/80">
                      <span className="text-green-400 mt-0.5 font-bold">✓</span>
                      <span>{indicator}</span>
                    </li>
                  ))}
                </ul>
                {analysis.indicators.length === 0 && (
                  <p className="text-white/60 text-sm">No specific indicators detected</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Citations Section */}
          <div className="rounded-xl bg-white/10 backdrop-blur-md border border-white/20 shadow-lg overflow-hidden">
            <button
              onClick={() => toggleSection("citations")}
              className="w-full flex items-center justify-between p-4 hover:bg-white/15 transition-colors duration-200"
            >
              <h3 className="text-white font-semibold">Detected Citations ({analysis.citations.length})</h3>
              <ChevronDown
                className={`w-5 h-5 text-white/60 transition-transform duration-300 ${
                  expandedSections.has("citations") ? "rotate-180" : ""
                }`}
              />
            </button>

            {expandedSections.has("citations") && (
              <div className="border-t border-white/10 p-4 space-y-3 max-h-96 overflow-y-auto">
                {analysis.citations.length > 0 ? (
                  analysis.citations.map((citation, idx) => (
                    <div
                      key={citation.id}
                      className="p-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors duration-200"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium mb-1">{citation.text}</p>
                          <p className="text-white/60 text-xs">Type: {citation.type}</p>
                          <p className="text-white/50 text-xs mt-1">Line {citation.lineNumber}</p>
                        </div>
                        <button
                          onClick={() => toggleBookmark(idx)}
                          className="p-1.5 rounded bg-white/10 hover:bg-white/15 border border-white/20 text-white/70 transition-all duration-200"
                          title="Bookmark for later"
                        >
                          <Bookmark className={`w-4 h-4 ${bookmarkedSections.has(idx) ? "fill-white/70" : ""}`} />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-white/60 text-sm">No citations detected in this document</p>
                )}
              </div>
            )}
          </div>

          {/* Key Phrases Section */}
          <div className="rounded-xl bg-white/10 backdrop-blur-md border border-white/20 shadow-lg overflow-hidden">
            <button
              onClick={() => toggleSection("phrases")}
              className="w-full flex items-center justify-between p-4 hover:bg-white/15 transition-colors duration-200"
            >
              <h3 className="text-white font-semibold">Key Phrases ({analysis.keyPhrases.length})</h3>
              <ChevronDown
                className={`w-5 h-5 text-white/60 transition-transform duration-300 ${
                  expandedSections.has("phrases") ? "rotate-180" : ""
                }`}
              />
            </button>

            {expandedSections.has("phrases") && (
              <div className="border-t border-white/10 p-4">
                <div className="flex flex-wrap gap-2">
                  {analysis.keyPhrases.length > 0 ? (
                    analysis.keyPhrases.map((phrase, idx) => (
                      <button
                        key={idx}
                        onClick={() => copyToClipboard(phrase)}
                        className="px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 border border-white/20 text-white/90 text-sm transition-all duration-200 flex items-center gap-1"
                        title="Click to copy"
                      >
                        <span>{phrase}</span>
                        <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                      </button>
                    ))
                  ) : (
                    <p className="text-white/60 text-sm">No key phrases extracted</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Preview */}
      <div className="rounded-xl bg-white/10 backdrop-blur-md border border-white/20 shadow-lg overflow-hidden">
        <button
          onClick={() => toggleSection("content")}
          className="w-full flex items-center justify-between p-4 hover:bg-white/15 transition-colors duration-200"
        >
          <h3 className="text-white font-semibold">Document Content Preview</h3>
          <ChevronDown
            className={`w-5 h-5 text-white/60 transition-transform duration-300 ${
              expandedSections.has("content") ? "rotate-180" : ""
            }`}
          />
        </button>

        {expandedSections.has("content") && (
          <div className="border-t border-white/10 p-4">
            <div className="max-h-64 overflow-y-auto">
              <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">
                {showFullContent ? content : contentPreview}
              </p>
              {content.length > 500 && (
                <button
                  onClick={() => setShowFullContent(!showFullContent)}
                  className="mt-3 flex items-center gap-2 px-3 py-1.5 text-white/70 hover:text-white/90 text-sm transition-colors"
                >
                  {showFullContent ? (
                    <>
                      <EyeOff className="w-4 h-4" />
                      Show less
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      Show full content
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions Footer */}
      <div className="flex gap-3 pt-2">
        {onImport && (
          <button
            onClick={onImport}
            className="flex-1 px-4 py-2.5 bg-blue-500/30 hover:bg-blue-500/40 border border-blue-400/50 text-white rounded-lg transition-all duration-200 font-medium"
          >
            Import to Document
          </button>
        )}
        <button
          onClick={onReset}
          className="flex-1 px-4 py-2.5 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-lg transition-all duration-200 font-medium"
        >
          Upload Another File
        </button>
        <button
          onClick={() => copyToClipboard(content)}
          className="flex-1 px-4 py-2.5 bg-white/15 hover:bg-white/25 border border-white/30 text-white rounded-lg transition-all duration-200 font-medium"
        >
          Copy Full Content
        </button>
      </div>
    </div>
  );
}
